import { useCallback, useRef } from 'react';
import { isEmpty } from 'lodash';
import { MULTI } from '../../../general/Constants';
import { AGENT_IDS_FIELD_NAME, SELECTED_MODE } from '../../ServiceTestsConstants';

/**
 * Per-adapter selection history persistence via refs.
 *
 * Saves and restores agents.ids when switching between adapter types (default/wireless)
 * so users don't lose their selections. Mirrors portal/ui testsDetails.controller.js behavior
 * of preserving per-adapter agent selections.
 *
 * The 'multi' adapter is treated as transparent/non-owning:
 * - Entering multi: saves current selection under the outgoing non-multi adapter
 *   but does NOT modify agents.ids (multi uses the parent Wi-Fi Group Test's agents).
 * - Leaving multi: restores from the target non-multi adapter's history if available;
 *   otherwise preserves current form data (e.g. fresh from server after save+reopen).
 *
 * @state-ownership
 * - Owns: selectionHistoryRef (useRef), prevAdapterTypeRef (useRef)
 * - Reads from RHF: agents.ids via getValuesRef
 * - Writes to RHF: agents.ids via setValueRef
 * - Params: { getValuesRef, setValueRef, modeRef, previousNotEmptyAgentsIds }
 */
export function useSelectionHistory({ getValuesRef, setValueRef, modeRef, previousNotEmptyAgentsIds }) {
    // Per-adapter selection history. Keys created on demand; undefined = never saved.
    const selectionHistoryRef = useRef({});
    // undefined = first render; set to adapterType after first effect run
    const prevAdapterTypeRef = useRef(undefined);

    /**
     * Save current selection under the old adapter key and restore saved selection
     * for the new adapter key. Must be called synchronously at the top of the
     * fetch effect (before the async API call).
     *
     * @param {string} adapterType - The new adapter type being switched to
     */
    const saveAndRestore = useCallback(
        (adapterType) => {
            const prevAdapterType = prevAdapterTypeRef.current;
            const isFirstRender = prevAdapterType === undefined;

            // First try with current ids, if current ids is emtpy, then try last selected not empty version
            const getAgentsValueIds = () => {
                const agentsValueIds = getValuesRef.current(AGENT_IDS_FIELD_NAME) || [];
                if (!isEmpty(agentsValueIds)) {
                    return agentsValueIds;
                }
                return previousNotEmptyAgentsIds || [];
            };

            if (!isFirstRender && modeRef.current === SELECTED_MODE) {
                if (adapterType === MULTI) {
                    // Entering multi: save current IDs under the outgoing non-multi adapter.
                    // Do NOT modify agents.ids — multi is transparent (NGP-3487).
                    selectionHistoryRef.current[prevAdapterType] = getAgentsValueIds();
                } else if (prevAdapterType === MULTI) {
                    // Leaving multi: restore from target adapter only if history exists.
                    // If no history (e.g. save+reopen), preserve current form data from server.
                    const savedIds = selectionHistoryRef.current[adapterType];
                    if (savedIds !== undefined) {
                        setValueRef.current(AGENT_IDS_FIELD_NAME, savedIds);
                    }
                } else {
                    // Normal non-multi transition (default ↔ wireless)
                    const prevAdapterTypeAgentsValueIds = getAgentsValueIds();
                    selectionHistoryRef.current[prevAdapterType] = prevAdapterTypeAgentsValueIds;

                    // First try with history adapterType AgentsValueIds
                    let currentAdapterTypeAgentsValueIds = selectionHistoryRef.current[adapterType] || [];
                    // If not found history adapterType AgentsValueIds, fallback to previous not empty adapterType AgentsValueIds
                    if (isEmpty(currentAdapterTypeAgentsValueIds) && !isEmpty(prevAdapterTypeAgentsValueIds)) {
                        currentAdapterTypeAgentsValueIds = prevAdapterTypeAgentsValueIds;
                    }
                    setValueRef.current(AGENT_IDS_FIELD_NAME, currentAdapterTypeAgentsValueIds);
                }
            }

            prevAdapterTypeRef.current = adapterType;
        },
        [modeRef, getValuesRef, previousNotEmptyAgentsIds, setValueRef]
    );

    return useCallback((newAdapterType) => saveAndRestore(newAdapterType), [saveAndRestore]);
}
